import React, { Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect, useState } from 'react';
import { User } from '../Types/user';
import { getUserSession, IsLoggedIn, setUserSetting } from '../Services/user';
import Popup, { ConfirmPopup, ConfirmPopupProps } from '../Components/Popup/Popup';
import BizContextMenu, { BizContextMenuConfig, ContextMenuItem } from '../Components/Mini/BizContextMenu';
import { BizMediabankProvider } from '../Components/BizMediabank/BizMediabankContext';
import { StatusState } from '../Hooks/useBizQuery';
import { getCultureCode, getCultureId, getLocalStorage, setLocalStorage } from '../Services/common';
import { Unit } from '../Types/unit';
import { dispatchBusEvent, EventContextProvider } from './eventContext';
import { Culture } from '../Types/culture';
import { Filter } from '../Types/admin/filterList';
import { getGuid } from '../Utils/stringUtils';

export type AdminState = {
	selectedCulture?: Culture;
	filterHistory?: Record<string, Filter | null>;
};
type GlobalState = {
	user?: User;
	cultureId: number;
	cultureCode: string;
	extendSessionWithoutAction: boolean;
	pageDirty: boolean;
	pageDirtySaveFn?: () => void;
	pageDirtySaveState?: StatusState;
	isMobileMenuOpen: boolean;
	routingBasePath: string;
	legacy?: boolean;
	limitedAccessModeUnit?: Unit;
	hasAcceptedCookies?: boolean;
	hasAcceptedStatisticsCookies?: boolean;
	hasAcceptedMarketingCookies?: boolean;
	adminState?: AdminState;
};

const initialState: GlobalState = {
	pageDirty: false,
	extendSessionWithoutAction: false,
	isMobileMenuOpen: false,
	routingBasePath: '/',
	cultureId: getCultureId(),
	cultureCode: getCultureCode(),
};

type ConfirmOptions = Omit<ConfirmPopupProps, 'onCancel'> & {
	title?: string;
};

const GlobalStateContext = createContext<{
	state: GlobalState;
	setState: Dispatch<SetStateAction<GlobalState>>;
	showModal: (component: ReactNode, title: string, onClose?: () => void, adminPopup?: boolean) => () => void;
	hideModal: () => void;
	showConfirm: (options: ConfirmOptions) => void;
	openContextMenu: (event: React.MouseEvent, menuItems: ContextMenuItem[], element?: unknown, cssClass?: string) => void;
	setDirty: (dirty: boolean) => void;
	registerPageDirtySaveFn: (fn: () => void, saveState: StatusState) => void;
	clearPageDirtySaveFn: () => void;
	toggleMobileMenu: () => void;
	openMobileMenu: () => void;
	closeMobileMenu: () => void;
	getRoutingPathWBase: (path: string) => string;
	reInitializeState: () => Promise<void>;
	setHasAcceptedCookies: (accepted: boolean) => Promise<void>;
	setCookieCategoryConsent: (category: 'statistics' | 'marketing', accepted: boolean) => Promise<void>;
	updateAdminState: (adminState: Partial<AdminState>) => void;
}>({
	state: initialState,
	setState: () => {},
	showModal: () => () => {},
	hideModal: () => {},
	showConfirm: () => {},
	openContextMenu: () => {},
	setDirty: () => {},
	registerPageDirtySaveFn: () => {},
	clearPageDirtySaveFn: () => {},
	toggleMobileMenu: () => {},
	openMobileMenu: () => {},
	closeMobileMenu: () => {},
	getRoutingPathWBase: (path: string) => initialState.routingBasePath + path,
	reInitializeState: () => new Promise(() => {}),
	setHasAcceptedCookies: () => new Promise(() => {}),
	setCookieCategoryConsent: () => new Promise(() => {}),
	updateAdminState: () => {},
});

type GlobalProviderProps = {
	children: ReactNode;
	legacy?: boolean;
	routingBasePath?: string;
	customDispatch?: (type: string, payload?: unknown) => void;
	initialAdminState?: AdminState;
};

export const GlobalStateProvider = ({
	children,
	legacy,
	routingBasePath,
	customDispatch,
	initialAdminState,
}: GlobalProviderProps) => {
	const [state, setState] = useState<GlobalState>({
		...initialState,
		legacy: legacy,
		routingBasePath: routingBasePath ?? initialState.routingBasePath,
		adminState: initialAdminState,
	});
	const [modal, setModal] = useState<
		{ id: string; title: string; content: ReactNode; onClose?: () => void; adminPopup?: boolean }[]
	>([]);
	const [contextMenuConfig, setContextMenuConfig] = useState<BizContextMenuConfig | null>(null);

	const Initialize = (): Promise<void> =>
		new Promise((resolve) => {
			console.log('GlobalStateProvider: Initialize');
			getUserSession().then((userSession) => {
				console.log('userSession', userSession);
				if (userSession) {
					setState((prevState) => ({
						...prevState,
						user: userSession.User,
						cultureId: userSession.CultureId,
						cultureCode: userSession.CultureCode,
						extendSessionWithoutAction: userSession.ExtendSessionWithoutAction,
						limitedAccessModeUnit: userSession.LimitedAccessMode?.Unit ?? undefined,
					}));
				}
				resolve();
			});
		});

	useEffect(() => {
		Initialize();
	}, []);

	const reInitializeState = (): Promise<void> => {
		return new Promise((resolve) => {
			Initialize().then(() => {
				dispatchBusEvent('REINITIALIZE');
				customDispatch?.('REINITIALIZE');
				resolve();
			});
		});
	};

	const updateAdminState = (adminState: Partial<AdminState>) => {
		setState((prevState) => {
			if (customDispatch) {
				customDispatch('adminStateChanged', adminState);
			}
			return {
				...prevState,
				adminState: {
					...prevState.adminState,
					...adminState,
				},
			};
		});
	};

	const toggleMobileMenu = () => {
		setState((prevState) => ({
			...prevState,
			isMobileMenuOpen: !prevState.isMobileMenuOpen,
		}));
	};

	const openMobileMenu = () => {
		setState((prevState) => ({
			...prevState,
			isMobileMenuOpen: true,
		}));
	};

	const closeMobileMenu = () => {
		setState((prevState) => ({
			...prevState,
			isMobileMenuOpen: false,
		}));
	};

	const setDirty = (dirty: boolean) => {
		setState((prevState) => ({
			...prevState,
			pageDirty: dirty,
		}));
	};

	const showModal = (component: ReactNode, title: string, onClose?: () => void, adminPopup?: boolean) => {
		const id = getGuid();
		setModal((prev) => [...prev, { id, title, content: component, onClose, adminPopup }]);
		// Close only this modal, including when its owner unmounts beneath another popup.
		return () => {
			onClose?.();
			setModal((prev) => prev.filter((item) => item.id !== id));
		};
	};

	const hideModal = () => {
		const modalToClose = modal.length > 0 ? modal[modal.length - 1] : null;
		if (modalToClose?.onClose) {
			modalToClose.onClose();
		}
		setModal((prev) => prev.slice(0, -1));
	};

	const showConfirm = (options: ConfirmOptions) => {
		const onConfirmWrapper = () => {
			hideModal();
			options.onConfirm();
		};

		const onCancel = () => {
			hideModal();
		};

		const confirmComponent = (
			<ConfirmPopup
				message={options.message}
				onConfirm={onConfirmWrapper}
				onCancel={onCancel}
				legacy={options.legacy ?? state.legacy}
			/>
		);
		showModal(confirmComponent, options.title ?? 'Confirm', undefined, false);
	};

	const openContextMenu = (event: React.MouseEvent, menuItems: ContextMenuItem[], element?: unknown, cssClass?: string) => {
		event.preventDefault();
		event.stopPropagation();
		setContextMenuConfig({
			mouseX: event.clientX - 2,
			mouseY: event.clientY - 4,
			menuItems,
			element,
			onClose: () => {
				setContextMenuConfig(null);
			},
			cssClass,
		});
	};

	const setHasAcceptedCookies = async (accepted: boolean) => {
		const HAS_ACCEPTED_COOKIES_KEY = 'HasAcceptedCookies';
		const HAS_ACCEPTED_STATISTICS_COOKIES_KEY = 'HasAcceptedStatisticsCookies';
		const HAS_ACCEPTED_MARKETING_COOKIES_KEY = 'HasAcceptedMarketingCookies';
		setLocalStorage(HAS_ACCEPTED_COOKIES_KEY, String(accepted));
		setLocalStorage(HAS_ACCEPTED_STATISTICS_COOKIES_KEY, String(accepted));
		setLocalStorage(HAS_ACCEPTED_MARKETING_COOKIES_KEY, String(accepted));
		if (state.user && IsLoggedIn(state.user)) {
			await Promise.all([
				setUserSetting(HAS_ACCEPTED_COOKIES_KEY, String(accepted)),
				setUserSetting(HAS_ACCEPTED_STATISTICS_COOKIES_KEY, String(accepted)),
				setUserSetting(HAS_ACCEPTED_MARKETING_COOKIES_KEY, String(accepted)),
			]);
		}
		setState((prevState) => {
			const newState = {
				...prevState,
				hasAcceptedCookies: accepted,
				hasAcceptedStatisticsCookies: accepted,
				hasAcceptedMarketingCookies: accepted,
			};
			if (customDispatch) {
				customDispatch('hasAcceptedCookiesChanged', accepted);
			}
			return newState;
		});
	};

	const setCookieCategoryConsent = async (category: 'statistics' | 'marketing', accepted: boolean) => {
		const key = category === 'statistics' ? 'HasAcceptedStatisticsCookies' : 'HasAcceptedMarketingCookies';
		const otherKey = category === 'statistics' ? 'HasAcceptedMarketingCookies' : 'HasAcceptedStatisticsCookies';
		const stateKey = category === 'statistics' ? 'hasAcceptedStatisticsCookies' : 'hasAcceptedMarketingCookies';
		const storedOtherConsent = getLocalStorage(otherKey);
		const otherAccepted = (storedOtherConsent ?? getLocalStorage('HasAcceptedCookies')) === 'true';
		const hasAcceptedAll = accepted && otherAccepted;
		setLocalStorage(key, String(accepted));
		if (storedOtherConsent === null) setLocalStorage(otherKey, String(otherAccepted));
		setLocalStorage('HasAcceptedCookies', String(hasAcceptedAll));
		if (state.user && IsLoggedIn(state.user)) {
			await Promise.all([
				setUserSetting(key, String(accepted)),
				...(storedOtherConsent === null ? [setUserSetting(otherKey, String(otherAccepted))] : []),
				setUserSetting('HasAcceptedCookies', String(hasAcceptedAll)),
			]);
		}
		setState((prevState) => ({
			...prevState,
			[stateKey]: accepted,
			hasAcceptedCookies: hasAcceptedAll,
		}));
		if (customDispatch) customDispatch('cookieConsentChanged', { category, accepted });
	};

	const registerPageDirtySaveFn = (fn: () => void, saveState: StatusState) => {
		//console.log('registerPageDirtySaveFn');
		setState((prevState) => ({
			...prevState,
			pageDirtySaveFn: fn,
			pageDirtySaveState: saveState,
		}));
	};

	const clearPageDirtySaveFn = () => {
		//console.log('clearPageDirtySaveFn');
		setState((prevState) => ({
			...prevState,
			pageDirtySaveFn: undefined,
			pageDirtySaveState: undefined,
		}));
	};

	const getRoutingPathWBase = (path: string) => {
		if (state.routingBasePath.endsWith('/')) {
			return state.routingBasePath + path.replace(/^\//, '');
		} else {
			return state.routingBasePath + '/' + path.replace(/^\//, '');
		}
	};

	return (
		<GlobalStateContext.Provider
			value={{
				state,
				setState,
				showModal,
				hideModal,
				showConfirm,
				openContextMenu,
				setDirty,
				registerPageDirtySaveFn,
				clearPageDirtySaveFn,
				toggleMobileMenu,
				openMobileMenu,
				closeMobileMenu,
				getRoutingPathWBase,
				reInitializeState,
				setHasAcceptedCookies,
				setCookieCategoryConsent,
				updateAdminState,
			}}
		>
			<EventContextProvider customDispatch={customDispatch}>
				<BizMediabankProvider>
					{children}
					{modal.length > 0 &&
						modal.map((m) => (
							<Popup key={m.id} title={m.title} adminPopup={m.adminPopup}>
								{m.content}
							</Popup>
						))}
					{contextMenuConfig !== null && <BizContextMenu {...contextMenuConfig} />}
				</BizMediabankProvider>
			</EventContextProvider>
		</GlobalStateContext.Provider>
	);
};

export const useGlobalState = () => {
	const context = useContext(GlobalStateContext);
	if (!context) {
		throw new Error('useGlobalState must be used within a GlobalStateProvider');
	}
	return context;
};
